// src/components/LiveChatSupport.js
import React from 'react';
import styles from './LiveChatSupport.module.css';

const LiveChatSupport = () => {
    return (
        <div className={styles.container}>
            <section className={styles.hero}>
                {/* Blurred background image div */}
                <div className={styles.herobackground}></div>

                <h1>Provide Instant, 24/7 AI Live Chat Support for Your Customers</h1>
                <p>
                    Engage your customers in real-time with intelligent AI live chat support that understands intent, answers instantly, and connects to human agents when needed.
                </p>
                <div className={styles.ctaButtons}>
                    <button className={styles.primaryButton}>Get Started</button>
                    <button className={styles.secondaryButton}>Request a Demo</button>
                </div>
            </section>

            <section className={styles.whyChoose}>
                <h2>Why Choose AI Live Chat Support?</h2>
                <div className={styles.cardContainer}>
                    <div className={styles.card}>
                        <h3>Always Available</h3>
                        <p>AI live chat operates 24/7, ensuring no customer waits.</p>
                    </div>
                    <div className={styles.card}>
                        <h3>Smart Conversations</h3>
                        <p>Natural language AI adapts to customer queries instantly.</p>
                    </div>
                    <div className={styles.card}>
                        <h3>Hybrid Model</h3>
                        <p>AI handles routine tasks, live chat agents solve complex cases.</p>
                    </div>
                    <div className={styles.card}>
                        <h3>Scalable & Cost-Effective</h3>
                        <p>Manage thousands of conversations without hiring more staff.</p>
                    </div>
                </div>
            </section>

            <section className={styles.keyFeatures}>
                <h2>Key Features of AI Live Chat Support</h2>
                <div className={styles.cardContainer}>
                    <div className={styles.card}>
                        <h3>Multichannel Support</h3>
                        <p>Real-time multichannel live chat (Website, WhatsApp, Facebook, Slack)</p>
                    </div>
                    <div className={styles.card}>
                        <h3>Intent Detection</h3>
                        <p>AI-driven intent detection for accurate answers</p>
                    </div>
                    <div className={styles.card}>
                        <h3>Knowledge Base</h3>
                        <p>Knowledge base integration for self-service solutions</p>
                    </div>
                    <div className={styles.card}>
                        <h3>Instant Escalation</h3>
                        <p>Instant escalation to human live chat agents</p>
                    </div>
                    <div className={styles.card}>
                        <h3>Analytics & Reporting</h3>
                        <p>Analytics & reporting to track performance and customer satisfaction</p>
                    </div>
                </div>
            </section>

            <section className={styles.howItWorks}>
                <h2>How AI Live Chat Support Works</h2>
                <ul>
                    <li>Customer opens the live chat widget on your website or app.</li>
                    <li>AI instantly responds with accurate answers.</li>
                    <li>Complex questions are smoothly escalated to a human live chat agent.</li>
                    <li>AI learns from every interaction, improving over time.</li>
                </ul>
            </section>

            <section className={styles.securityCompliance}>
                <h2>AI Live Chat Built with Enterprise-Grade Security</h2>
                <ul>
                    <li>ISO, GDPR, CCPA, CPRA compliant</li>
                    <li>End-to-end encrypted live chat conversations</li>
                    <li>Independent audits for data protection & reliability</li>
                </ul>
            </section>

            <section className={styles.integrations}>
                <h2>Integrations</h2>
                <ul>
                    <li>CRM: Salesforce, Zoho, HubSpot</li>
                    <li>Helpdesk & Ticketing: Zendesk, Freshdesk, ServiceNow</li>
                    <li>Messaging Platforms: WhatsApp, Messenger, Slack, Telegram</li>
                    <li>AI & Cloud: Microsoft, OpenAI</li>
                </ul>
            </section>

            <section className={styles.industries}>
                <h2>Industries That Benefit from AI Live Chat</h2>
                <ul>
                    <li>Ecommerce & Retail: AI chatbots + live chat for order tracking & product help</li>
                    <li>Banking & Finance: Secure AI chat for FAQs, instant escalation for live agent verification</li>
                    <li>Healthcare: Patient FAQs, appointment scheduling & live nurse chat handoff</li>
                    <li>Travel & Hospitality: Booking confirmations, itinerary changes & 24/7 live chat support</li>
                </ul>
            </section>

            <section className={styles.successStories}>
                <h2>Success Stories</h2>
                <div className={styles.cardContainer}>
                    <div className={styles.card}>
                        <h3>Retailer</h3>
                        <p>40% faster resolutions with AI live chat + fewer escalations</p>
                    </div>
                    <div className={styles.card}>
                        <h3>Banking</h3>
                        <p>Automated FAQs reduced calls by 55%</p>
                    </div>
                    <div className={styles.card}>
                        <h3>Healthcare</h3>
                        <p>60% shorter wait times with AI-powered chat triage</p>
                    </div>
                    <div className={styles.card}>
                        <h3>Ecommerce</h3>
                        <p>30% higher repeat purchases via proactive live chat</p>
                    </div>
                </div>
            </section>

            <section className={styles.testimonials}>
                <h2>Trusted by Businesses, Loved by Customers</h2>
                <div className={styles.cardContainer}>
                    <div className={styles.card}>
                        <p>“AI live chat transformed our support. Customers get answers instantly.” – Retail Brand</p>
                    </div>
                    <div className={styles.card}>
                        <p>“Seamless WhatsApp + AI live chat helped us boost engagement.” – Banking Client</p>
                    </div>
                    <div className={styles.card}>
                        <p>“Our team handles fewer queries now – AI filters the rest.” – Ecommerce Leader</p>
                    </div>
                </div>
            </section>

            <section className={styles.benefits}>
                <h2>Benefits of AI Live Chat Support</h2>
                <div className={styles.cardContainer}>
                    <div className={styles.card}>
                        <h3>Reduce Response Times</h3>
                        <p>From minutes to seconds</p>
                    </div>
                    <div className={styles.card}>
                        <h3>Cut Costs</h3>
                        <p>By up to 50%</p>
                    </div>
                    <div className={styles.card}>
                        <h3>Improve CSAT & NPS</h3>
                        <p>Customer Satisfaction & Net Promoter Score</p>
                    </div>
                    <div className={styles.card}>
                        <h3>Future-Proof</h3>
                        <p>With continuous AI learning and live agent collaboration</p>
                    </div>
                </div>
            </section>

            <section className={styles.callToAction}>
                <h2>Upgrade to AI Live Chat Support Today</h2>
                <p>Deliver real-time, AI-powered chat support that never sleeps and scales with your business.</p>
                <div className={styles.ctaButtons}>
                    <button className={styles.primaryButton}>Schedule a Demo</button>
                    <button className={styles.secondaryButton}>Talk to an Expert</button>
                </div>
            </section>

            <section className={styles.recognition}>
                <h2>Award-Winning AI Live Chat Support Solution</h2>
                <ul>
                    <li>Featured by top tech analysts</li>
                    <li>Certified for compliance and data security</li>
                    <li>Powered by partnerships with Microsoft & OpenAI</li>
                </ul>
            </section>

            <footer className={styles.footer}>
                <p>&copy; 2023 AI Live Chat Support. All rights reserved.</p>
            </footer>
        </div>
    );
};

export default LiveChatSupport;

